import {Type} from 'class-transformer';
import {Contact} from './contact';
import {serializeType} from './common';
import {ToDo} from './to-do';
import {UniqueChargeCode} from '@app/shared/classes//charge-code';
import {PartnerCreditCard} from "@app/shared/classes//partner-credit-card";
import {PartnerAchDetail} from "@app/shared/classes//partner-ach-detail";
import {PartnerPaymentSetting} from "@app/shared/classes/partner-payment-setting";
import {ApprovalStatusType} from '../enums/approval-status-type.enum';
import {CurrentTxnStatus} from "../classes/current-txn-status";
import {OrgAddress} from "../classes/org-address";
import {isNullOrUndefined} from "@qntm-code/utils";
import {AddressType, TradingPartnerType} from "@app/shared";

export enum OrgType {
  Payroll = 'payroll_customer',           // onelynk customer of Payroll module
  OneLynk = 'onelynk_customer',           // customer of HCM, Finance, and/or Procurement module
  Customer = 'CUSTOMER',           // trading partner - customer
  Vendor = 'VENDOR',                      // trading partner - vendor
  Org = 'ORG',
}

export enum TPPaymentType {
  Bank = "Bank",
  Credit = "Credit"
}

export enum ExternalContactSubtype {
  Partner = 'PARTNER'
}

export enum TaxNumber {
  Ssn = 'ssn',
  Ein = 'ein'
}

export enum ToDoType {
  Vendor = 'VENDOR',
  Customer = 'CUSTOMER',
  Contract = 'CONTRACT',
  Project = 'PROJECT',
  ProjectEstimate = 'PROJECT_ESTIMATE'
}

export enum StandardPaymentTerm {
  cod = 'COD',
  net15 = 'NET15',
  net30 = 'NET30',
  net45 = 'NET45',
  net60 = 'NET60'
}

export enum LastDays {
  last30 = 30,
  last60 = 60,
  last90 = 90,
  last120 = 120,
  last150 = 150
}

export enum SecurityCodeLength {
  Amex = 4,
  Others = 3
}

export enum SalesTaxRateType {
  Single = "Single",
  Combined = "Combined"
}

export enum PaymentCardType {
  Amex = 'AMEX',
  Mastercard = 'MASTERCARD',
  Visa = 'VISA',
  Discover = 'DISCOVER'
}

export class PartnerAddress {
  id: number;
  version?: number;
  locationCode?: string;
  addrType?: AddressType;
  addrName?: string;

  addrLn1: string;
  addrLn2: string;
  city: string;
  stateCode: string;
  postalCode: string;
  country: string;

  mainPhone?: string;
  faxPhone?: string;
  partnerId: number;

  constructor (partnerId?: number) {
    if (partnerId) {
      this.partnerId = partnerId;
    }
  }
}


export class SalesTaxDate {
  id: number;
  version: number;
  taxYear: number;
  agencyTaxRate: number;
  effectiveDate: string;
  salesTaxAgencyId: number;
}

export class SalesTaxAgency {
  id: number;
  version: number;
  taxAgency: string;
  description: string;
  taxPaymentType: string;
  calendarId: number;
  @Type(serializeType(SalesTaxDate)) salesTaxDates: SalesTaxDate[];
  startOn: string;
}

export class SalesTaxRateItem {
  id: number;
  version?: number;
  salesTaxRateId?: number;
  @Type(serializeType(SalesTaxAgency)) salesTaxAgency: SalesTaxAgency;
}


export class SalesTaxRate {
  id: number;
  version?: number;
  name: string;
  taxRateType: string;
  description: string;
  compositeTaxRate: number;
  @Type(serializeType(SalesTaxRateItem)) salesTaxRateItems: SalesTaxRateItem[];
}


// private PartnerAddress address;
//
// // For serialization
// private Long partnerId;
// private Long salesTaxRateId;
// private Long addressId;
//
// // Transient properties
// private String addrName;

export class PartnerSalesTax {
  id: number;
  version: number;
  partnerId: number;
  addressId: number;
  addrName: string;
  salesTaxRate: SalesTaxRate;
  salesTaxRateId: number;

  @Type(serializeType(SalesTaxRate)) salesTaxRateDetail?: SalesTaxRate;

  constructor (partnerId?: number) {
    if (partnerId) {
      this.partnerId = partnerId;
    }
  }
}

export class PartnerContact extends Contact {
  partnerId: number;
  addrName: string;
  partnerAddressId: number;
  addressId: number;
}

export class AdditionalInfos {
  id: number;
  version?: number;
  columnId: number;
  columnValue: string;
  columnName: string;
}

export class PartnerAdditionalInfo extends AdditionalInfos {
  partnerId: number;
}

export class TradingPartnerAccountInfo {
  ledgerAccountId: number;
  accountNumber: number;
  accountName: string;
  accountDescription: string;
  accountId: number;
}

// export class TradingPartnerAccountSetting {
//   id: number;
//   version: number;
//   accountId: number;
//   partnerId: number;
//   @Type(serializeType(TradingPartnerAccountInfo)) accountInfo: TradingPartnerAccountInfo;
// }



export class TradingPartner {
  id: number;
  version: number;
  subtype: TradingPartnerType;
  dunsNumber: string;
  taxNumber: string;
  governmentAgency: boolean;
  require1099?: boolean;
  active: boolean = true;
  currentBalance: number;
  isEmployee: boolean;
  billingAddressSameAsMainAddress: boolean;
  billingContactSameAsMain: boolean;
  businessName: string;
  dbaName: string;
  enableDBA: boolean;
  website: string;
  userId: number;
  cageCode: string;
  submittedById: number;
  submittedForId: number;
  currentStatusName: ApprovalStatusType;
  hasSubmissionError?: boolean;
  pdfTemplateId: number;
  isSsn: boolean;
  allowTimesheetAccess?: boolean;
  allowExpenseReportAccess?: boolean;
  hasMainContact?: boolean;
  hasBillingContact?: boolean;
  hasBillToAddress?: boolean;
  achCustomerDetails: any;
  txnNumber?: string;

  @Type(serializeType(OrgAddress)) orgAddress?: OrgAddress;
  @Type(serializeType(PartnerPaymentSetting)) partnerPaymentSettings?: PartnerPaymentSetting[];
  @Type(serializeType(PartnerCreditCard)) partnerCreditCards?: PartnerCreditCard[];
  @Type(serializeType(PartnerAchDetail)) partnerAchDetails?: PartnerAchDetail[];

  @Type(serializeType(PartnerAddress)) partnerAddresses?: PartnerAddress[];
  @Type(serializeType(PartnerSalesTax)) partnerSalesTaxes?: PartnerSalesTax[];
  @Type(serializeType(PartnerContact)) partnerContacts?: PartnerContact[];
  @Type(serializeType(PartnerAdditionalInfo)) partnerAdditionalInfos?: PartnerAdditionalInfo[];
  @Type(serializeType(ToDo)) partnerTodoTasks?: ToDo[];

  constructor (subtype?: TradingPartnerType) {
    if (!isNullOrUndefined(subtype)) {
      this.subtype = subtype;
    }
    this.partnerAddresses = new Array<PartnerAddress>();
  }
}

export class ComplianceSchedule {
  id: number;
  version: number;
  name: string;
  subtype: string;
  year: string;
  start: string;
}

export class PaymentTerm {
  id: number;
  orgId: number;
  version: number;
  paymentTermType: string;
  standardTermName: string;
  paymentTermsName: string;
  discountIfPaidEarly: number;
  pastDueInterestRate: number;
  daysDueAfterBill: number;
  daysToPayForDiscount: number;
  monthlyTermName: string;
  dayofMonthBillDue: number;
  dayofMonthForDiscount: number;
  dayofMonthForNextMonth: number;
  active: boolean;
}

export class TradingPartnerInfo {
  id: number;
  active: boolean;
  currentStatus: string;
  subtype: TradingPartnerType;

  businessName: string;
  orgId: number;
  pdfTemplateId: number;
  userId: number;
}

export class TradingPartnerSummary {
  id: number;
  active: boolean;
  currentStatus: ApprovalStatusType;
  businessName: string;
  fullName: string;
  title: string;
  pdfTemplateId: number;

  currentTxnStatus: CurrentTxnStatus;           // populated by frontend to determine whether it can be edited/deleted based on the currentStatus
}

export class TradingPartnerTransactionSummary {
  transactionType: string;
  docNumber: string;
  refID: number;
  date?: string;
  amount: number;
  active: boolean;
  businessName: string;
  @Type(serializeType(UniqueChargeCode)) uniqueChargeCodes: UniqueChargeCode[];
}

export class CorrespondencePost {
  id: number;
  version: number;
  subject: string;
  postText: string;
  contactId: number;
  userId: number;
  correspondenceId: number;
  userName: string;
  @Type(serializeType(Date)) displayCreatedOn: Date;
}

export class CorrespondenceCategory {
  id: number;
  version: number;
  name: string;
  description: string;
}

export class Correspondence {
  id: number;
  version: number;
  topic: string;
  refId: string;
  contactId: number;
  correspondCategoryId: number;
  contactName: string;
  categoryName: string;
  @Type(serializeType(Date)) displayModifiedOn: Date;
  @Type(serializeType(CorrespondencePost)) correspondencePosts: CorrespondencePost[] = [];

  message: string;
}

export enum TradingPartnerTabIndex {
  GeneralInfoTab = 0,
  ContactTab = 1,
  PaymentTab = 2,
  AdditionalInfoTab = 3,
  CorrespondenceTab = 4,
  ToDoTab = 5,
  TaxInfoTab = 6,      //only customer have this tab
  VendorAttachmentTab = 6,
  CustomerAttachmentTab = 7,
  VendorAccountSettingTab = 7,       //only vendor have this tab
  CustomerAccountSettingTab = 8,
  VendorLaborCategoryTab = 8
}
